import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from "../api";
import { normalizeMediaUrl } from "../lib/media";

/**
 * Comments UI
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - target: { id: string } | null
 *  - isRepost: boolean
 *  - inline: boolean (if true, renders inline under a post instead of overlay)
 *  - onCommentPosted: function(targetId, createdComment)  // NEW: called after successful POST
 */
export default function CommentsDrawer({ open, onClose, target, isRepost, inline = false, onCommentPosted }) {
  
  const token = localStorage.getItem('token') || '';
  const me = (() => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
})();
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [items, setItems] = useState([]);      // comments list
  const [next, setNext] = useState(null);      // pagination cursor (created_at)
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState('');

  const resetState = () => {
    setItems([]);
    setNext(null);
    setText('');
  };

  const fetchComments = async (reset = false) => {
    if (!open || !target?.id) return;
    if (loading) return;
    setLoading(true);
    try {
      const base = isRepost ? `/reposts/${target.id}/comments` : `/posts/${target.id}/comments`;
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (!reset && next) params.set('before', next);

      const config = {};
      if (token) config.headers = headers;

      const { data } = await api.get(`${base}?${params.toString()}`, config);
      const list = data?.comments || [];
      setItems(prev => (reset ? list : [...prev, ...list]));
      setNext(data?.next || null);
    } catch (e) {
      console.error('load comments error', e?.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  const addComment = async (e) => {
    e?.preventDefault?.();
    if (!token) return alert('Login required.');
    const t = text.trim();
    if (!t) return;
    try {
      setPosting(true);
      const base = isRepost ? `/reposts/${target.id}/comments` : `/posts/${target.id}/comments`;
      const { data } = await api.post(base, { text: t }, { headers });
      if (data?.comment) {
        // add to local list immediately
        setItems(prev => [data.comment, ...prev]);
        setText('');
        // notify parent so it can increment comment_count optimistically
        try {
          onCommentPosted && onCommentPosted(target.id, data.comment);
        } catch (err) {
          console.warn('onCommentPosted callback error', err);
        }
      }
    } catch (e) {
      console.error('comment post error', e?.response?.data || e.message);
      alert(e?.response?.data?.error || e.message || 'Failed to comment');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (c) => {
    if (!token) return alert('Login required.');
    if (!window.confirm('Delete this comment?')) return;
    try {
      if (isRepost) {
        await api.delete(`/reposts/${target.id}/comments/${c.id}`, {   headers });
      } else {
        await api.delete(`/posts/${target.id}/comments/${c.id}`, {   headers });
      }
      setItems(prev => prev.filter(x => x.id !== c.id));
    } catch (e) {
      console.error('delete comment error', e?.response?.data || e.message);
      alert(e?.response?.data?.error || e.message || 'Delete failed');
    }
  };

  // Load when opened/target changes
  useEffect(() => {
    if (!open) return;
    resetState();
    const t = setTimeout(() => fetchComments(true), 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.id, isRepost]);

  if (!open) return null;

  // --- Inline variant (under the post) ---
  if (inline) {
    return (
      <div
        role="region"
        aria-label="Comments"
        style={{
          marginTop: 8,
          border: '1px solid #2a2f3a',
          borderRadius: 10,
          background: '#0f1218',
          color: '#d9e2ec',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ padding: 10, borderBottom: '1px solid #2a2f3a', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{isRepost ? 'Repost comments' : 'Comments'}</div>
          <button className="btn btn-ghost" onClick={onClose} style={{ marginLeft: 'auto' }} aria-label="Close">✕</button>
        </div>

        {/* List */}
<div
  style={{
    padding: 10,
    display: 'grid',
    gap: 10,
    maxHeight: 320,
    overflowY: 'auto',
    overscrollBehavior: 'contain'
  }}
>
          {items.map((c) => (
            <div key={c.id} style={{ border: '1px solid #2a2f3a', borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 12, color: '#8aa0b4', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={normalizeMediaUrl(c.profile_pic_url) || '/uploads/default-avatar.png'}
                  alt=""
                  style={{ width: 22, height: 22, borderRadius: 999, objectFit: 'cover' }}
                />
                {/* Prefer username; fall back to handle if username missing */}
                <strong>{c.username || (c.user_id ? `@${c.user_id}` : 'User')}</strong>
                {c.username && c.user_id && <span style={{ color: '#6b7f93' }}>@{c.user_id}</span>}
                <span>• {new Date(c.created_at).toLocaleString()}</span>
                {(
  String(c.user_id) === String(me?.user_id) ||
  String(target?.author_id) === String(me?.user_id)
) && (
  <button
    className="btn btn-ghost"
    onClick={() => deleteComment(c)}
    style={{ marginLeft: "auto" }}
  >
    Delete
  </button>
)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
            </div>
          ))}

          {!items.length && !loading && <div className="subtle">No comments yet</div>}

          {next && (
            <button className="btn btn-ghost" disabled={loading} onClick={() => fetchComments(false)}>
              {loading ? 'Loading…' : 'Load older'}
            </button>
          )}
          {!next && items.length > 0 && <div className="subtle">No more</div>}
        </div>

        {/* Composer */}
        <form onSubmit={addComment} style={{ padding: 10, borderTop: '1px solid #2a2f3a', display: 'flex', gap: 8 }}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a comment…"
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" type="submit" disabled={posting || !text.trim()}>
            {posting ? 'Posting…' : 'Send'}
          </button>
        </form>
      </div>
    );
  }

  // --- (overlay variant kept, unused now) ---
  return (
    <div
      aria-hidden={!open}
      style={{
        position: 'fixed', inset: 0, display: 'flex',
        alignItems: 'stretch', justifyContent: 'flex-end',
        background: 'rgba(0,0,0,0.45)', zIndex: 50
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-label="Comments"
        style={{
          width: 'min(560px, 100%)', height: '100%',
          background: '#0f1218', borderLeft: '1px solid #2a2f3a',
          color: '#d9e2ec', display: 'flex', flexDirection: 'column'
        }}
      >
        <div style={{ padding: 12, borderBottom: '1px solid #2a2f3a', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{isRepost ? 'Repost comments' : 'Comments'}</div>
          
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'grid', gap: 10 }}>
          {items.map((c) => (
            <div key={c.id} style={{ border: '1px solid #2a2f3a', borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 13, color: '#8aa0b4', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={c.profile_pic_url || 'https://via.placeholder.com/28'}
                  alt=""
                  style={{ width: 24, height: 24, borderRadius: 999, objectFit: 'cover' }}
                />
                <strong>{c.username || (c.user_id ? `@${c.user_id}` : 'User')}</strong>
                {c.username && c.user_id && <span style={{ color: '#6b7f93' }}>@{c.user_id}</span>}
                <span>• {new Date(c.created_at).toLocaleString()}</span>
                {(
  String(c.user_id) === String(me?.user_id) ||
  String(target?.author_id) === String(me?.user_id)
) && (
  <button
    className="btn btn-ghost"
    onClick={() => deleteComment(c)}
    style={{ marginLeft: "auto" }}
  >
    Delete
  </button>
)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
            </div>
          ))}
          {!items.length && !loading && <div className="subtle">No comments yet</div>}
          {next && (
            <button className="btn btn-ghost" disabled={loading} onClick={() => fetchComments(false)}>
              {loading ? 'Loading…' : 'Load older'}
            </button>
          )}
        </div>
        <form onSubmit={addComment} style={{ padding: 12, borderTop: '1px solid #2a2f3a', display: 'flex', gap: 8 }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…" style={{ flex: 1 }} />
          <button className="btn btn-primary" type="submit" disabled={posting || !text.trim()}>{posting ? 'Posting…' : 'Send'}</button>
        </form>
      </div>
    </div>
  );
}